import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EventApi, EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { format } from 'date-fns';

import { Label, LABEL_TYPE } from '@/components/generic';
import { queryClient } from '@/configuration/react-query';
import { dateAndHoursFormatRequest } from '@/constants';
import { DeviceScheduleContentType } from '@/enums/DeviceScheduleContentType';
import { ArrowDownHeavyIcon, ArrowUpHeavyIcon, CalendarIcon } from '@/resources';
import {
  useCreateDeviceScheduleMutation,
  useGetDeviceScheduleByScreenIdQuery
} from '@/services/deviceSchedule.service';
import { TestIds } from '@/shared/test-ids';
import {
  CreateEditDeviceSchedulePayload,
  DeviceSchedule,
  TimeSlotFormValues
} from '@/types/DeviceScheduleTypes';

import { TimeSlotModal } from './TimeSlotModal';

type ScreenCalendarProps = {
  screenId: number;
  selectedEventId?: string | null;
  onEventSelect?: (eventId: string | null) => void;
  setRefetch?: (refetchFn: () => void) => void;
};

const INLINE_EVENT_DURATION_THRESHOLD_MINUTES = 70;

export const ScreenCalendar = ({
  screenId,
  selectedEventId: selectedEventIdFromParent,
  onEventSelect,
  setRefetch
}: ScreenCalendarProps) => {
  const [t] = useTranslation('common');
  const createScheduleMutation = useCreateDeviceScheduleMutation(queryClient, {
    onSuccess: () => {
      refetchDeviceSchedules();
      setModalDate(null);
    },
    onError: () => {
      undefined;
    }
  });
  const { data: deviceSchedules, refetch: refetchDeviceSchedules } =
    useGetDeviceScheduleByScreenIdQuery(Number(screenId));
  const [events, setEvents] = useState<EventInput[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const calendarRef = useRef<FullCalendar | null>(null);
  const topCalendarRef = useRef<FullCalendar | null>(null);
  const [currentMonthTitle, setCurrentMonthTitle] = useState('');
  const [topCalendarKey, setTopCalendarKey] = useState(0);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [modalDate, setModalDate] = useState<Date | null>(null);

  const dayMonth = selectedDate.toLocaleDateString('en-GB', {
    month: 'long',
    day: 'numeric'
  });

  useEffect(() => {
    if (setRefetch) {
      setRefetch(() => refetchDeviceSchedules);
    }
  }, [setRefetch, refetchDeviceSchedules]);

  const year = selectedDate.getFullYear();

  const getDeviceScheduleTitle = (schedule: DeviceSchedule): string | null => {
    if (!schedule.contentType || schedule.contentType === DeviceScheduleContentType.Default) {
      return null;
    }

    if (schedule.contentType === DeviceScheduleContentType.Playlist) {
      return schedule.playlist?.name || null;
    }

    if (
      schedule.contentType === DeviceScheduleContentType.NewAsset ||
      schedule.contentType === DeviceScheduleContentType.EventAsset ||
      schedule.contentType === DeviceScheduleContentType.OrganizationAsset
    ) {
      return schedule.asset?.assetName || null;
    }

    return null;
  };

  useEffect(() => {
    setSelectedDate(new Date());
    const api = calendarRef.current?.getApi();
    if (api) {
      api.gotoDate(new Date());
    }
    if (!selectedEventIdFromParent) {
      setSelectedEventId(null);
    }
    if (onEventSelect && !selectedEventIdFromParent) {
      onEventSelect(null);
    }
  }, [screenId, onEventSelect, selectedEventIdFromParent]);

  useEffect(() => {
    setSelectedEventId(selectedEventIdFromParent ?? null);
  }, [selectedEventIdFromParent]);

  useEffect(() => {
    if (!selectedEventIdFromParent || events.length === 0) {
      return;
    }

    const selectedEvent = events.find((event) => String(event.id) === selectedEventIdFromParent);

    if (!selectedEvent?.start) {
      return;
    }

    let startDate: Date;

    if (typeof selectedEvent.start === 'string' || typeof selectedEvent.start === 'number') {
      startDate = new Date(selectedEvent.start);
    } else if (selectedEvent.start instanceof Date) {
      startDate = selectedEvent.start;
    } else if (Array.isArray(selectedEvent.start)) {
      startDate = new Date(selectedEvent.start[0]);
    } else {
      return;
    }

    if (isNaN(startDate.getTime())) {
      return;
    }

    setSelectedDate(startDate);
    const dayCalendarApi = calendarRef.current?.getApi();

    if (dayCalendarApi) {
      dayCalendarApi.gotoDate(startDate);
    }
  }, [events, selectedEventIdFromParent]);

  useEffect(() => {
    if (!deviceSchedules?.data) {
      setEvents([]);
      return;
    }

    const calendarEvents: EventInput[] = deviceSchedules.data.map((schedule: DeviceSchedule) => {
      const title = getDeviceScheduleTitle(schedule);
      return {
        ...schedule,
        ...(title && { title })
      };
    });

    setEvents(calendarEvents);
  }, [deviceSchedules, t]);

  useEffect(() => {
    setTopCalendarKey((k) => k + 1);
  }, [events, selectedDate]);

  const handleDateClick = (info: DateClickArg) => {
    setSelectedDate(info.date);
    const api = calendarRef.current?.getApi();
    if (api) {
      api.gotoDate(info.date);
    }
    setSelectedEventId(null);
    if (onEventSelect) {
      onEventSelect(null);
    }
  };

  const handlePrevMonth = () => {
    const api = topCalendarRef.current?.getApi();
    if (api) {
      api.prev();
    }
  };
  const handleNextMonth = () => {
    const api = topCalendarRef.current?.getApi();
    if (api) {
      api.next();
    }
  };

  const handleAssign = (values: TimeSlotFormValues) => {
    const start = new Date(values.date ?? new Date());
    const end = new Date(values.date ?? new Date());

    if (values.allDay) {
      start.setHours(0, 0, 1, 0);
      end.setHours(23, 59, 59, 0);
    } else {
      const [startH, startM] = values.startTime.split(':').map(Number);
      start.setHours(startH, startM, 0, 0);

      const [endH, endM] = values.endTime.split(':').map(Number);
      end.setHours(endH, endM, 0, 0);
    }

    const payload: CreateEditDeviceSchedulePayload = {
      screenId: screenId,
      start: format(start, dateAndHoursFormatRequest),
      end: format(end, dateAndHoursFormatRequest),
      allDay: values.allDay
    };

    createScheduleMutation.mutate(payload);
  };

  const shouldUseInlineLayout = (event: EventApi) => {
    if (event.allDay || !event.start || !event.end) {
      return false;
    }

    const durationMinutes = (event.end.getTime() - event.start.getTime()) / (1000 * 60);
    return durationMinutes < INLINE_EVENT_DURATION_THRESHOLD_MINUTES;
  };

  return (
    <div data-testid={TestIds.digitalSignage.calendarPanel} className='flex size-full flex-col p-4'>
      <div className='flex flex-col md:px-2'>
        <div className='flex flex-row gap-x-1'>
          <Label text={t(dayMonth) + ', '} type={LABEL_TYPE.H2} />
          <Label text={`${year}`} type={LABEL_TYPE.H2} className='!text-secondary-gray' />
        </div>
        <Label
          text={t(selectedDate.toLocaleDateString('en-GB', { weekday: 'long' }))}
          type={LABEL_TYPE.PARAGRAPH1}
        />
      </div>

      <div className='flex items-center justify-between md:px-2'>
        <div className='text-lg font-semibold'>{currentMonthTitle}</div>
        <div className='flex gap-2'>
          <button onClick={handleNextMonth} className='rounded-lg p-1 hover:bg-gray-300'>
            <ArrowDownHeavyIcon className='mb-1 size-6 fill-current text-primary-blue' />
          </button>
          <button onClick={handlePrevMonth} className='rounded-lg p-1 hover:bg-gray-300'>
            <ArrowUpHeavyIcon className='size-6 fill-current text-primary-blue' />
          </button>
        </div>
      </div>
      <div
        data-testid={TestIds.digitalSignage.monthCalendar}
        className='min-h-0 flex-[1] border-b-2 border-primary-gray p-2'>
        <FullCalendar
          key={topCalendarKey}
          ref={topCalendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView='dayGridMonth'
          headerToolbar={false}
          initialDate={selectedDate}
          events={events}
          dateClick={handleDateClick}
          dayCellClassNames={(arg) => {
            const dayHasEvent = events?.some((e) => {
              if (!e.start) {
                return false;
              }
              let eventDate: Date;
              if (typeof e.start === 'string' || typeof e.start === 'number') {
                eventDate = new Date(e.start);
              } else if (e.start instanceof Date) {
                eventDate = e.start;
              } else if (Array.isArray(e.start)) {
                eventDate = new Date(e.start[0]);
              } else {
                return false;
              }
              return (
                eventDate instanceof Date &&
                !isNaN(eventDate.getTime()) &&
                eventDate.toDateString() === arg.date.toDateString()
              );
            });
            return [
              arg.date.toDateString() === selectedDate.toDateString() ? 'selected-day' : '',
              dayHasEvent ? 'fc-has-events' : ''
            ];
          }}
          height='100%'
          datesSet={(info) => setCurrentMonthTitle(info.view.title)}
        />
      </div>

      <div
        data-testid={TestIds.digitalSignage.dayCalendar}
        className='relative mt-2 min-h-0 flex-[2]'>
        <FullCalendar
          ref={calendarRef}
          plugins={[timeGridPlugin, interactionPlugin]}
          initialView='timeGridDay'
          headerToolbar={false}
          slotMinTime='00:00:00'
          slotMaxTime='24:00:00'
          allDaySlot={true}
          height='100%'
          events={events}
          slotLabelContent={(arg) => {
            const hour = arg.date.getHours() % 12 || 12;
            const ampm = arg.date.getHours() < 12 ? 'AM' : 'PM';
            return (
              <div className='fc-timegrid-slot-label-cushion' data-ampm={ampm}>
                {hour}
              </div>
            );
          }}
          nowIndicator={true}
          nowIndicatorContent={() => {
            const now = new Date();
            const hours = now.getHours().toString().padStart(2, '0');
            const minutes = now.getMinutes().toString().padStart(2, '0');

            return (
              <div className='relative w-full'>
                <div className='absolute inset-x-0 top-1/2 h-px bg-accent-red'></div>

                <div className='absolute -top-2 left-0 rounded-md bg-accent-red px-1 py-0.5 text-xs font-semibold text-white shadow-md'>
                  {hours}:{minutes}
                </div>
              </div>
            );
          }}
          dateClick={(info) => {
            setModalDate(info.date);
            setSelectedEventId(null);
            if (onEventSelect) {
              onEventSelect(null);
            }
          }}
          eventContent={(arg) => {
            const isSelected = selectedEventId === arg.event.id;
            if (arg.event.allDay) {
              return (
                <div className='flex w-full'>
                  <div
                    className={`flex w-full cursor-pointer flex-row gap-6 rounded-md border-none bg-primary-gray p-2 ${isSelected ? 'border-2 border-dashed border-primary-blue' : 'hover:border-2 hover:border-dashed hover:border-primary-blue'}`}>
                    <div className='flex flex-row gap-2'>
                      <div className='relative flex size-8 items-center justify-center'>
                        <CalendarIcon className='size-6 text-primary-blue' />
                        <span className='absolute bottom-[4px] text-2xs font-bold text-primary-blue'>
                          {arg.event.start ? new Date(arg.event.start).getDate() : ''}
                        </span>
                      </div>
                      <Label
                        text={`${arg.event.title ? arg.event.title : t('contentNotAssign')}`}
                        type={LABEL_TYPE.PARAGRAPH2}
                        className={`${
                          arg.event.title
                            ? 'font-medium text-primary-blue'
                            : 'text-secondary-blue-gray'
                        }`}
                      />
                    </div>
                    <Label
                      text={'All day'}
                      type={LABEL_TYPE.PARAGRAPH2}
                      className='!text-secondary-dark-steel-blue'
                    />
                  </div>
                </div>
              );
            }
            const start = arg.event.start;
            const end = arg.event.end;

            const formatTime = (date: Date | null) => {
              if (!date) {
                return '';
              }
              let hours = date.getHours();
              const minutes = date.getMinutes();
              const ampm = hours >= 12 ? 'PM' : 'AM';
              hours = hours % 12 || 12;
              return `${hours}${minutes ? `:${minutes.toString().padStart(2, '0')}` : ''}${ampm}`;
            };

            const timeRange = `${formatTime(start)} - ${formatTime(end)}`;

            return (
              <div className='flex size-full flex-row gap-2 p-1'>
                <div className='flex w-[4px] rounded-md bg-primary-blue'></div>
                <div className='event-content-wrapper flex min-w-0 flex-1 flex-col justify-center gap-1 overflow-hidden'>
                  <div
                    className={`truncate ${arg.event.title ? 'font-medium text-primary-blue' : 'text-primary-blue-48'} `}>
                    {arg.event.title || t('contentNotAssign')}
                  </div>
                  <div className='event-time text-sm text-primary-blue-48'>{timeRange}</div>
                </div>
              </div>
            );
          }}
          eventClick={(info) => {
            const newSelectedId = selectedEventId === info.event.id ? null : info.event.id;
            setSelectedEventId(newSelectedId);

            if (onEventSelect) {
              onEventSelect(newSelectedId);
            }
          }}
          eventClassNames={(arg) => {
            const classes = [];

            if (arg.event.id === selectedEventId) {
              classes.push('event-selected');
            }

            if (arg.event.allDay) {
              classes.push('all-day-event');
            }

            if (shouldUseInlineLayout(arg.event)) {
              classes.push('event-inline');
            }

            return classes;
          }}
          eventDidMount={function (info) {
            info.el.dataset.testid = `${TestIds.digitalSignage.scheduleEvent}-${info.event.id}`;
            if (info.event.allDay) {
              info.el.classList.add('all-day-event');
            }
          }}
        />
        {modalDate && (
          <TimeSlotModal
            key={modalDate?.toISOString()}
            date={modalDate}
            onClose={() => setModalDate(null)}
            onAssign={(values) => handleAssign(values)}
          />
        )}
      </div>
    </div>
  );
};
